/**
 * Phone Signup - Mad green/black theme.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast, useI18n } from '../../providers';
import { getPhoneValidation, formatPhoneInput, getFullPhoneNumber, validateCameroonPhone } from '../../utils/phoneValidation';

const PhoneSignup = ({ onSuccess, transactionId }) => {
  const navigate = useNavigate();
  const { signupWithPhone, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [formData, setFormData] = useState({
    countryCode: '+237',
    phone: '',
    fullName: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [phoneValidation, setPhoneValidation] = useState(getPhoneValidation('+237'));

  const countryCodes = [
    { code: '+237', country: 'Cameroon', flag: '🇨🇲', name: language === 'fr' ? 'Cameroun' : 'Cameroon' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', name: 'Kenya' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬', name: 'Uganda' },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿', name: 'Tanzania' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼', name: 'Rwanda' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', name: 'Nigeria' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭', name: 'Ghana' },
  ];

  useEffect(() => {
    setPhoneValidation(getPhoneValidation(formData.countryCode));
  }, [formData.countryCode]);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      setFormData((prev) => ({ ...prev, [field]: formatPhoneInput(value, formData.countryCode) }));
    } else if (field === 'countryCode') {
      setFormData((prev) => ({ ...prev, [field]: value, phone: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    const cleanedPhone = formData.phone.replace(/\s/g, '');

    if (!formData.fullName.trim()) {
      newErrors.fullName = language === 'fr' ? 'Le nom complet est requis' : 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = language === 'fr' ? 'Le nom doit contenir au moins 2 caractères' : 'Name must be at least 2 characters';
    }
    if (!cleanedPhone) {
      newErrors.phone = language === 'fr' ? 'Le numéro de téléphone est requis' : 'Phone number is required';
    } else if (formData.countryCode === '+237') {
      const validation = validateCameroonPhone(cleanedPhone);
      if (!validation.valid) newErrors.phone = validation.error;
    } else {
      const validation = getPhoneValidation(formData.countryCode);
      if (!validation.pattern.test(cleanedPhone) || cleanedPhone.length !== validation.length) {
        newErrors.phone = validation.error;
      }
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = language === 'fr' ? 'Vous devez accepter les conditions' : 'You must agree to the terms';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const phoneNumber = getFullPhoneNumber(formData.phone, formData.countryCode);
      const countryCode = formData.countryCode.replace('+', '');
      await signupWithPhone({
        phone: phoneNumber,
        fullName: formData.fullName.trim(),
        countryCode: countryCode === '237' ? 'CM' : countryCode === '254' ? 'KE' : 'CM',
      });
      showSuccess(language === 'fr' ? 'Code de vérification envoyé!' : 'Verification code sent!');
      if (onSuccess) onSuccess(phoneNumber);
      else navigate('/auth/verify-otp', { state: { phone: phoneNumber, transactionId } });
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || (language === 'fr' ? 'Échec de l\'envoi du code' : 'Failed to send verification code');
      showError(errorMessage);
    }
  };

  return (
    <div className="auth-mad">
      <div className="auth-mad-bg" aria-hidden="true" />
      <div className="auth-mad-grid" aria-hidden="true" />
      <div className="auth-mad-inner">
        <button type="button" onClick={() => navigate(-1)} className="auth-mad-back">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          <span>{language === 'fr' ? 'Retour' : 'Back'}</span>
        </button>

        <div className="auth-mad-card">
          <div className="auth-mad-header">
            <div className="auth-mad-icon" aria-hidden>📱</div>
            <h1 className="auth-mad-title">
              {language === 'fr' ? 'Entrez votre numéro' : 'Enter your phone number'}
            </h1>
            <p className="auth-mad-sub">
              {language === 'fr' ? 'Nous vous enverrons un code de vérification' : "We'll send you a verification code"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="auth-mad-form">
            <div className="auth-mad-field">
              <label className="auth-mad-label">{language === 'fr' ? 'Nom complet' : 'Full Name'}</label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
                required
                className={errors.fullName ? 'border-red-500' : ''}
                aria-invalid={!!errors.fullName}
              />
              {errors.fullName && <p className="auth-mad-error">{errors.fullName}</p>}
            </div>

            <div className="auth-mad-field">
              <label className="auth-mad-label">{language === 'fr' ? 'Votre numéro' : 'Your number'}</label>
              <div className="auth-mad-phone-row">
                <div className="auth-mad-select-wrap">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => handleChange('countryCode', e.target.value)}
                    className="auth-mad-select"
                    aria-label="Country code"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                </div>
                <div className="auth-mad-input-wrap">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder={phoneValidation.placeholder}
                    required
                    inputMode="numeric"
                    className={errors.phone ? 'border-red-500' : ''}
                    aria-invalid={!!errors.phone}
                  />
                  {errors.phone && <p className="auth-mad-error">{errors.phone}</p>}
                  {formData.phone && !errors.phone && (
                    <p className="auth-mad-hint">{language === 'fr' ? 'Exemple' : 'Example'}: {phoneValidation.example}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="auth-mad-terms">
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleChange('agreeToTerms', e.target.checked)}
                  aria-invalid={!!errors.agreeToTerms}
                />
                <span>
                  {language === 'fr' ? (
                    <>J'accepte les <a href="/terms" target="_blank" rel="noopener noreferrer">Conditions d'utilisation</a> et la <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a></>
                  ) : (
                    <>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></>
                  )}
                </span>
              </label>
              {errors.agreeToTerms && <p className="auth-mad-error">{errors.agreeToTerms}</p>}
            </div>

            <button type="submit" className="auth-mad-btn" disabled={loading}>
              {loading ? (language === 'fr' ? 'Envoi…' : 'Sending…') : (language === 'fr' ? 'Envoyer le code →' : 'Send Code →')}
            </button>

            <div className="auth-mad-divider" />

            <div className="auth-mad-footer">
              <p>
                {language === 'fr' ? 'Déjà inscrit?' : 'Already registered?'}
                <button type="button" onClick={() => navigate('/auth/login', { state: { transactionId } })} className="auth-mad-link">
                  {language === 'fr' ? 'Se connecter' : 'Login'}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PhoneSignup;
